import { useRef, useState, useEffect } from 'react';
import EmailEditor from 'react-email-editor';
import Button from '../common/Button';

/**
 * Email Template WYSIWYG Editor Component
 * Uses React-Email-Editor (unlayer) for drag-and-drop email template building
 */
export default function EmailTemplateEditor({
  templateType,
  initialDesign,
  initialHtml,
  onSave,
  onCancel,
  isSavingToDb = false
}) {
  const emailEditorRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Available template variables based on template type
  const mergeTags = {
    base_template: [
      { name: 'Company Name', value: '{{company_name}}' },
      { name: 'Current Year', value: '{{current_year}}' }
    ],
    signing_link: [
      { name: 'Trainer Name', value: '{{trainer_name}}' },
      { name: 'Contract Title', value: '{{contract_title}}' },
      { name: 'Signing URL', value: '{{signing_url}}' },
      { name: 'Expiry Date', value: '{{expiry_date}}' },
      { name: 'Company Name', value: '{{company_name}}' },
      { name: 'Current Year', value: '{{current_year}}' }
    ],
    reminder: [
      { name: 'Trainer Name', value: '{{trainer_name}}' },
      { name: 'Contract Title', value: '{{contract_title}}' },
      { name: 'Signing URL', value: '{{signing_url}}' },
      { name: 'Expiry Date', value: '{{expiry_date}}' },
      { name: 'Company Name', value: '{{company_name}}' },
      { name: 'Current Year', value: '{{current_year}}' }
    ],
    contract_signed: [
      { name: 'Trainer Name', value: '{{trainer_name}}' },
      { name: 'Contract Title', value: '{{contract_title}}' },
      { name: 'Signed At', value: '{{signed_at}}' },
      { name: 'Company Name', value: '{{company_name}}' },
      { name: 'Current Year', value: '{{current_year}}' }
    ],
    creator_notification: [
      { name: 'Trainer Name', value: '{{trainer_name}}' },
      { name: 'Trainer Email', value: '{{trainer_email}}' },
      { name: 'Contract Title', value: '{{contract_title}}' },
      { name: 'Signed At', value: '{{signed_at}}' },
      { name: 'Contract URL', value: '{{contract_url}}' },
      { name: 'Company Name', value: '{{company_name}}' },
      { name: 'Current Year', value: '{{current_year}}' }
    ]
  };

  const editorConfig = {
    displayMode: 'email',
    tools: {
      image: { enabled: true },
      text: { enabled: true },
      button: { enabled: true },
      divider: { enabled: true },
      html: { enabled: true },
      social: { enabled: false },
      menu: { enabled: false },
      timer: { enabled: false },
      video: { enabled: false }
    },
    appearance: {
      theme: 'light',
      panels: {
        tools: {
          dock: 'left'
        }
      }
    },
    mergeTags: mergeTags[templateType] || []
  };

  const onReady = () => {
    setIsLoading(false);
    
    // Load initial design if available and valid
    if (initialDesign && emailEditorRef.current) {
      try {
        const design = typeof initialDesign === 'string'
          ? JSON.parse(initialDesign)
          : initialDesign;
        
        // Only load if design has required structure
        if (design && design.body) {
          emailEditorRef.current.editor.loadDesign(design);
        }
      } catch (error) {
        console.error('Failed to load design:', error);
        // Editor will start with blank template on error
      }
    }
  };

  const handleSave = () => {
    if (!emailEditorRef.current) return;

    setIsSaving(true);
    
    emailEditorRef.current.editor.exportHtml((data) => {
      const { design, html } = data;
      
      onSave({
        design: JSON.stringify(design),
        html: html
      });
      
      setIsSaving(false);
    });
  };

  const handlePreview = () => {
    if (!emailEditorRef.current) return;

    emailEditorRef.current.editor.exportHtml((data) => {
      const { html } = data;
      
      // Open preview in new window with sample data
      const sampleData = getSampleData(templateType);
      const previewHtml = replaceVariables(html, sampleData);
      
      const previewWindow = window.open('', 'Email Preview', 'width=800,height=600');
      previewWindow.document.write(previewHtml);
      previewWindow.document.close();
    });
  };

  const getSampleData = (type) => {
    const baseData = {
      company_name: 'Trainers Contract System',
      current_year: new Date().getFullYear()
    };

    if (type === 'base_template') {
      return baseData;
    }

    return {
      ...baseData,
      trainer_name: 'John Doe',
      contract_title: 'Youth Team Training Contract 2024',
      signing_url: 'https://example.com/sign/abc123',
      expiry_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    };
  };

  const replaceVariables = (html, vars) => {
    let result = html;
    Object.keys(vars).forEach(key => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(regex, vars[key] || '');
    });
    return result;
  };

  const getTemplateTitle = (type) => {
    switch (type) {
      case 'base_template':
        return 'Base Email Template';
      case 'signing_link':
        return 'Contract Signing Email Template';
      case 'reminder':
        return 'Contract Reminder Email Template';
      case 'contract_signed':
        return 'Contract Signed Confirmation Email Template';
      case 'creator_notification':
        return 'Creator Notification Email Template';
      default:
        return 'Email Template';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {getTemplateTitle(templateType)}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Use the drag-and-drop editor to customize your email template. 
            Click &quot;Merge Tags&quot; in the editor to insert variables.
          </p>
        </div>
      </div>

      {/* Available Variables Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Available Variables:</h4>
        <div className="flex flex-wrap gap-2">
          {(mergeTags[templateType] || []).map((tag) => (
            <span 
              key={tag.value}
              className="inline-flex items-center px-2 py-1 rounded text-xs font-mono bg-blue-100 text-blue-800"
            >
              {tag.value}
            </span>
          ))}
        </div>
      </div>

      {/* Email Editor */}
      <div className="border border-gray-300 rounded-lg overflow-hidden" style={{ height: '600px' }}>
        <EmailEditor
          ref={emailEditorRef}
          onReady={onReady}
          options={editorConfig}
          minHeight="600px"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="secondary"
          onClick={handlePreview}
          disabled={isLoading}
        >
          Preview with Sample Data
        </Button>
        
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isSavingToDb}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || isSaving || isSavingToDb}
          >
            {isSavingToDb ? 'Saving to Database...' : isSaving ? 'Exporting...' : 'Save Template'}
          </Button>
        </div>
      </div>
    </div>
  );
}